import { useState } from 'react';

import RefreshIcon from '@mui/icons-material/Refresh';
import PropTypes from 'prop-types';

import Button from '@zenbusiness/zui/Button';

import { CLICKSTREAM_COMMON_PROPS } from '#client/components/utils/constants';

import SelectionCard from './components/SelectionCard';
import * as Styled from './SelectionPanel.style';

/**
 * Generic selection panel component for displaying a list of selectable items
 * Consolidates IdeasPanel and NamesPanel into a single reusable component
 *
 * @param {object} props - Component props
 * @param {Array} props.items - Array of items to display
 * @param {string|number} props.selectedItemId - ID of currently selected item
 * @param {Function} props.onItemSelect - Handler for item selection (receives itemId)
 * @param {Function} [props.getItemId] - Function to extract ID from item (default: item => item.id)
 * @param {Function} [props.getDisplayTitle] - Function to extract display title from item (default: item => item.title || item.name)
 * @param {Function} [props.onGenerateMore] - Handler for generating more items (optional, hides button if not provided)
 * @param {Function} [props.onStartOver] - Handler for starting over (optional, hides button if not provided)
 * @param {boolean} [props.loading=false] - Whether operations are in progress
 * @param {string} [props.generateMoreText='Generate more'] - Text for generate more button
 * @param {string} [props.startOverText='Start over'] - Text for start over button
 * @param {string} [props.clickstreamPrefix='selection'] - Prefix for clickstream element names
 * @component
 */
function SelectionPanel({
  items,
  selectedItemId,
  onItemSelect,
  getItemId = (item) => item.id,
  getDisplayTitle = (item) => item.title || item.name,
  onGenerateMore,
  onStartOver,
  loading = false,
  generateMoreText = 'Generate more',
  startOverText = 'Start over',
  clickstreamPrefix = 'selection'
}) {
  const [generateLoading, setGenerateLoading] = useState(false);
  const [startOverLoading, setStartOverLoading] = useState(false);

  const showGenerateMore = Boolean(onGenerateMore);
  const showStartOver = Boolean(onStartOver);
  const showButtons = showGenerateMore || showStartOver;

  const handleGenerateMore = async () => {
    if (generateLoading || startOverLoading || loading || !onGenerateMore) {
      return;
    }

    setGenerateLoading(true);
    try {
      await onGenerateMore();
    } catch (error) {
      console.error('Failed to generate more:', error);
    } finally {
      setGenerateLoading(false);
    }
  };

  const handleStartOver = async () => {
    if (startOverLoading || generateLoading || loading || !onStartOver) {
      return;
    }

    setStartOverLoading(true);
    try {
      await onStartOver();
    } catch (error) {
      console.error('Failed to start over:', error);
    } finally {
      setStartOverLoading(false);
    }
  };

  return (
    <>
      <Styled.SelectionList>
        {items.map((item) => {
          const itemId = getItemId(item);
          const displayTitle = getDisplayTitle(item);
          return (
            <SelectionCard
              key={itemId}
              clickstreamElementName={`${clickstreamPrefix}_select`}
              displayTitle={displayTitle}
              item={item}
              itemId={itemId}
              selected={itemId === selectedItemId}
              onClick={onItemSelect}
            />
          );
        })}
      </Styled.SelectionList>

      {showButtons && (
        <Styled.ButtonsContainer>
          {showGenerateMore && (
            <Button
              clickstreamProps={{
                ...CLICKSTREAM_COMMON_PROPS,
                elementName: `${clickstreamPrefix}_generate_more`,
                elementText: generateMoreText
              }}
              color="info"
              disabled={generateLoading || startOverLoading || loading}
              fullWidth
              loading={generateLoading}
              size="medium"
              startIcon={<RefreshIcon />}
              variant="outlined"
              onClick={handleGenerateMore}
            >
              {generateMoreText}
            </Button>
          )}
          {showStartOver && (
            <Button
              clickstreamProps={{
                ...CLICKSTREAM_COMMON_PROPS,
                elementName: `${clickstreamPrefix}_start_over`,
                elementText: startOverText
              }}
              disabled={generateLoading || startOverLoading || loading}
              fullWidth
              loading={startOverLoading}
              size="medium"
              variant="outlined"
              onClick={handleStartOver}
            >
              {startOverText}
            </Button>
          )}
        </Styled.ButtonsContainer>
      )}
    </>
  );
}

SelectionPanel.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedItemId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onItemSelect: PropTypes.func.isRequired,
  getItemId: PropTypes.func,
  getDisplayTitle: PropTypes.func,
  onGenerateMore: PropTypes.func,
  onStartOver: PropTypes.func,
  loading: PropTypes.bool,
  generateMoreText: PropTypes.string,
  startOverText: PropTypes.string,
  clickstreamPrefix: PropTypes.string
};

export default SelectionPanel;
